import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Play, Pause, Square, SkipBack, SkipForward, Volume2, VolumeX, Settings, RefreshCw, Loader2 } from 'lucide-react';
import { GlobalAudioService } from '@/services/audio/GlobalAudioService';
import { audioMetadataCache } from '@/services/audio/AudioMetadataCache';
import { useToast } from '@/hooks/use-toast';
import { SpeedPresetButtons } from './SpeedPresetButtons';
import { SectionNavigationButtons } from './SectionNavigationButtons';
import { Badge } from '@/components/ui/badge';
import { AudioDebugBadge } from './AudioDebugBadge';
import { performanceMonitor } from '@/services/PerformanceMonitorService';

interface WorldClassAudioPlayerProps {
  text: string;
  moduleId: string;
  sectionId: string;
  courseId: string;
  title?: string;
  onComplete?: () => void;
}

export const WorldClassAudioPlayer: React.FC<WorldClassAudioPlayerProps> = ({
  text,
  moduleId,
  sectionId,
  courseId,
  title = 'Audio Content',
  onComplete,
}) => {
  const [audioState, setAudioState] = useState(GlobalAudioService.getState());
  const [rate, setRate] = useState(1);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loadingTime, setLoadingTime] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initStartTime] = useState(Date.now());
  const { toast } = useToast();
  const mountedRef = React.useRef(true);
  
  // FIX: Debounce initialization to prevent rapid re-mounts (React StrictMode)
  const lastInitRef = React.useRef<{ moduleId: string; timestamp: number } | null>(null);

  // Track loading time for progressive states
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingTime(Date.now() - initStartTime);
    }, 500);
    
    return () => clearInterval(interval);
  }, [initStartTime]);

  useEffect(() => {
    console.log('🎧 WorldClassAudioPlayer mounted:', { 
      textLength: text.length, 
      moduleId, 
      sectionId 
    });

    // FIX: Debounce - Skip if same module was initialized within 500ms (React StrictMode fix)
    const now = Date.now();
    if (lastInitRef.current?.moduleId === moduleId && 
        now - lastInitRef.current.timestamp < 500) {
      console.log('⏭️ Skipping duplicate init (debounced within 500ms)');
      return;
    }
    lastInitRef.current = { moduleId, timestamp: now };

    // PHASE 2: Force re-initialization when module changes
    // Check if global audio is for a DIFFERENT module - stop it first
    const currentGlobalState = GlobalAudioService.getState();
    if (currentGlobalState.moduleId && currentGlobalState.moduleId !== moduleId) {
      console.log('🔄 Different module detected, stopping previous audio:', {
        previousModule: currentGlobalState.moduleId,
        currentModule: moduleId
      });
      GlobalAudioService.stop();
      // Clear stale cache for fresh audio fetch
      audioMetadataCache.forceRefresh(moduleId, courseId);
    }

    if (!text || text.trim().length === 0) {
      console.error('❌ No text provided to audio player');
      setIsInitializing(false);
      return;
    }

    if (!moduleId) {
      console.warn('⚠️ No moduleId provided, using "default"');
    }
    
    // PHASE 3: Bulletproof audio initialization with comprehensive error handling
    // PHASE 1 FIX: Defer audio init by 500ms to prioritize content rendering
    const initAudio = async () => {
      const operationId = `audio_${moduleId}_${Date.now()}`;
      performanceMonitor.startTimer(operationId);
      
      try {
        // Check if still mounted before initializing
        if (!mountedRef.current) {
          console.log('🚫 Component unmounting - skipping audio init');
          return;
        }
        
        // PHASE 3: Wrap in additional try-catch for GlobalAudioService call
        try {
          await GlobalAudioService.initializeFromDbAudio({
            text,
            moduleId,
            courseId,
            moduleTitle: title,
            courseTitle: title,
          });
        } catch (audioServiceError) {
          console.warn('[WorldClassAudioPlayer] GlobalAudioService init failed:', audioServiceError);
          throw audioServiceError; // Re-throw to be caught by outer catch
        }
        
        // Record successful audio load time
        const loadTime = performanceMonitor.endTimer(operationId, 'audio_load', { 
          moduleId, 
          courseId,
          success: true 
        });
        
        console.log(`🎵 Audio initialized in ${Math.round(loadTime)}ms`);
        
        // Check again after async operation
        if (mountedRef.current) {
          setIsInitializing(false);
        }
      } catch (error) {
        // PHASE 3: Never throw errors - always gracefully degrade
        console.warn('[WorldClassAudioPlayer] Audio init failed (non-critical):', error);
        
        // Record failed audio load
        performanceMonitor.endTimer(operationId, 'audio_load', { 
          moduleId, 
          courseId,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        
        if (mountedRef.current) {
          setIsInitializing(false);
        }
        // PHASE 3: Explicitly do NOT re-throw - let page render without audio
      }
    };

    // PHASE 5: Initialize audio immediately - no delay needed with caching
    initAudio();
    
    const unsubscribe = GlobalAudioService.subscribe((state) => {
      if (!mountedRef.current) return;
      console.log('🔄 Audio state changed:', state);
      setAudioState(state);
      setRate(state.rate);
      setVolume(state.volume);
      
      if (state.progress >= 100 && onComplete) {
        onComplete();
      }
    });

    return () => {
      console.log('🎧 WorldClassAudioPlayer unmounting');
      mountedRef.current = false;
      unsubscribe();
    };
  }, [text, moduleId, sectionId, courseId]);

  // PHASE 3: Bulletproof retry with comprehensive error handling
  const handleRetry = useCallback(() => {
    // Always clear cache for this module before retrying (fixes stale "no audio" cache)
    audioMetadataCache.forceRefresh(moduleId, courseId);
    console.log(`🔄 Cleared cache for ${moduleId} before retry`);
    
    // Clear error state so play button becomes enabled
    setAudioState(prev => ({ ...prev, error: null, isStopped: true }));
    setIsInitializing(true);
    setLoadingTime(0);
    
    // PHASE 3: Wrap retry in try-catch
    try {
      GlobalAudioService.initializeFromDbAudio({
        text,
        moduleId,
        courseId,
        moduleTitle: title,
        courseTitle: title,
      }).then(() => {
        setIsInitializing(false);
        toast({
          title: "Audio reloaded",
          description: "Audio player has been reloaded successfully."
        });
      }).catch((error) => {
        console.warn('[WorldClassAudioPlayer] Retry failed:', error);
        setIsInitializing(false);
        toast({
          title: "Retry failed",
          description: "Unable to load audio. Please try again later.",
          variant: "destructive"
        });
      });
    } catch (error) {
      console.warn('[WorldClassAudioPlayer] Retry initialization failed:', error);
      setIsInitializing(false);
    }
  }, [text, moduleId, courseId, title, toast]);

  // Force Start - clears all caches, re-fetches, and auto-plays
  const handleForceStart = useCallback(async () => {
    console.log('🚀 Force Start initiated - clearing all caches');
    
    // 1. Clear all caches
    audioMetadataCache.forceRefresh(moduleId, courseId);
    
    // 2. Clear error state and set initializing
    setAudioState(prev => ({ ...prev, error: null, isStopped: true }));
    setIsInitializing(true);
    setLoadingTime(0);
    
    try {
      // 3. Re-initialize audio from DB with fresh data
      await GlobalAudioService.initializeFromDbAudio({
        text,
        moduleId,
        courseId,
        moduleTitle: title,
        courseTitle: title,
      });
      
      setIsInitializing(false);
      
      // 4. Auto-play after successful initialization
      setTimeout(() => {
        console.log('🎵 Force Start: Auto-playing...');
        GlobalAudioService.play();
      }, 300);
      
      toast({
        title: "Audio ready",
        description: "Audio has been loaded and is playing."
      });
    } catch (error) {
      console.warn('[WorldClassAudioPlayer] Force Start failed:', error);
      setIsInitializing(false);
      toast({
        title: "Force Start failed",
        description: "Unable to load audio. The file may not exist.",
        variant: "destructive"
      });
    }
  }, [text, moduleId, courseId, title, toast]);

  const handlePlayPause = useCallback(() => {
    console.log('🎮 Play/Pause button clicked');
    
    if (audioState.isPlaying && !audioState.isPaused) {
      GlobalAudioService.pause();
    } else {
      GlobalAudioService.play();
    }
  }, [audioState.isPlaying, audioState.isPaused]);

  const handleStop = useCallback(() => {
    console.log('⏹️ Stop button clicked');
    GlobalAudioService.stop();
  }, []);

  const handleRateChange = useCallback((value: number[] | number) => {
    const newRate = Array.isArray(value) ? value[0] : value;
    setRate(newRate);
    GlobalAudioService.setRate(newRate);
  }, []);

  const handleVolumeChange = useCallback((value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    GlobalAudioService.setVolume(newVolume);
  }, []);

  const handleMuteToggle = useCallback(() => {
    const newVolume = isMuted ? 0.8 : 0;
    setVolume(newVolume);
    setIsMuted(!isMuted);
    GlobalAudioService.setVolume(newVolume);
  }, [isMuted]);

  const handleSkipBack = useCallback(() => {
    GlobalAudioService.skipBackward(30);
  }, []);

  const handleSkipForward = useCallback(() => {
    GlobalAudioService.skipForward(30);
  }, []);

  const handleProgressSeek = useCallback((value: number[]) => {
    const newProgress = value[0];
    const engine = GlobalAudioService.getEngine();
    if (!engine || !audioState) return;
    
    const newTime = (newProgress / 100) * audioState.duration;
    const wasPlaying = audioState.isPlaying;
    
    // Seek to new position
    GlobalAudioService.seekTo(newTime);
    
    // Resume playing if it was playing before
    if (wasPlaying) {
      setTimeout(() => GlobalAudioService.play(), 150);
    }
  }, [audioState]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = useCallback((time: number) => {
    GlobalAudioService.seekTo(time);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      
      switch (e.key.toLowerCase()) {
        case ' ':
          e.preventDefault();
          handlePlayPause();
          break;
        case 'arrowleft':
          e.preventDefault();
          handleSkipBack();
          break;
        case 'arrowright':
          e.preventDefault();
          handleSkipForward();
          break;
        case 's':
          e.preventDefault();
          handleStop();
          break;
        case 'm':
          e.preventDefault();
          handleMuteToggle();
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
          e.preventDefault();
          const speeds = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
          const speed = speeds[parseInt(e.key) - 1];
          if (speed) handleRateChange(speed);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handlePlayPause, handleSkipBack, handleSkipForward, handleStop, handleMuteToggle, handleRateChange]);

  // PHASE 3 & 4: Always show player with progressive loading/error states
  const renderPlayerStatus = () => {
    // Error state - show inline with retry and Force Start
    if (audioState.error) {
      const isNoAudio = audioState.error === 'No audio available';
      const isNetworkError = audioState.error.includes('Network') || audioState.error.includes('connection');
      
      return (
        <div className="space-y-2 py-2">
          <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
            <Volume2 className="h-4 w-4" />
            <span>
              {isNoAudio 
                ? '🎧 Audio for this lesson is being generated'
                : `⚠️ ${audioState.error}`}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Force Start - Primary action for errors */}
            <Button 
              onClick={handleForceStart} 
              size="sm" 
              className="gap-2 bg-green-600 hover:bg-green-700 text-white"
            >
              <Play className="h-4 w-4" />
              Force Start Audio
            </Button>
            
            <Button onClick={handleRetry} size="sm" variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              {isNoAudio ? 'Check Again' : 'Retry'}
            </Button>
            
            {isNetworkError && (
              <span className="text-xs text-muted-foreground self-center">
                Check your internet connection
              </span>
            )}
          </div>
        </div>
      );
    }

    // Loading state with buffering progress
    if (isInitializing || (!audioState.isReady && !audioState.error)) {
      const bufferingProgress = audioState.bufferingProgress || 0;
      
      if (loadingTime < 5000) {
        return (
          <div className="space-y-2 py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>
                {bufferingProgress > 0 
                  ? `Loading audio... ${bufferingProgress}%`
                  : 'Initializing audio player...'}
              </span>
            </div>
            {bufferingProgress > 0 && (
              <Progress value={bufferingProgress} className="h-1.5" />
            )}
          </div>
        );
      } else if (loadingTime < 15000) {
        return (
          <div className="space-y-2 py-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading audio... (checking database)</span>
            </div>
            {bufferingProgress > 0 && (
              <Progress value={bufferingProgress} className="h-1.5" />
            )}
          </div>
        );
      } else {
        return (
          <div className="space-y-2 py-2">
            <div className="flex items-center gap-2 text-sm text-orange-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>This is taking longer than expected...</span>
            </div>
            <Button onClick={handleRetry} size="sm" variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Force Reload
            </Button>
          </div>
        );
      }
    }

    return null;
  };

  // PHASE 3: REMOVED - No longer hide player completely
  // Player is ALWAYS visible with appropriate state indicators

  return (
    <>
      {/* Audio Debug Badge - Dev mode only */}
      <AudioDebugBadge
        moduleId={moduleId}
        courseId={courseId}
        audioUrl={audioState.audioUrl || null}
        isLoading={isInitializing}
        error={audioState.error}
        loadTime={loadingTime}
      />
      
      {/* PHASE 1: Always-visible Audio Player Card */}
      <Card className="relative overflow-hidden border border-border/50 bg-background">
        <div className="relative space-y-3 p-3">
          {/* Header - Title, Source Badge & Progress */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1">
              <h3 className="text-sm font-medium text-foreground truncate">{title}</h3>
              {!isInitializing && !audioState.error && (
                <Badge 
                  variant="outline" 
                  className={`gap-1 text-xs ${
                    audioState.source === 'tts' 
                      ? 'border-orange-500/50 bg-orange-500/10 text-orange-600 dark:text-orange-400'
                      : 'border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400'
                  }`}
                >
                  <Volume2 className="h-2.5 w-2.5" />
                  {audioState.source === 'tts' ? 'Fallback Audio' : 'Professional Audio'}
                </Badge>
              )}
            </div>
            {audioState.progress > 0 && (
              <div className="text-xs font-medium text-primary">
                {Math.round(audioState.progress)}%
              </div>
            )}
          </div>

          {/* PHASE 3: Always show status (loading or error) */}
          {renderPlayerStatus()}

          {/* Main Controls - Always visible */}
          <div className="flex items-center gap-2">
            {/* Skip Back */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={handleSkipBack}
              disabled={audioState.isStopped || isInitializing || !!audioState.error}
              aria-label="Skip back 10 seconds"
            >
              <SkipBack className="h-4 w-4" />
            </Button>

            {/* Play/Pause Button - disabled until audio is ready */}
            <Button
              size="icon"
              className="h-10 w-10 rounded-full shadow-md hover:shadow-lg transition-all"
              onClick={handlePlayPause}
              disabled={isInitializing || !!audioState.error || !audioState.isReady}
              aria-label={audioState.isPlaying && !audioState.isPaused ? 'Pause' : 'Play'}
            >
              {!audioState.isReady && !audioState.error && !isInitializing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : audioState.isPlaying && !audioState.isPaused ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </Button>

            {/* Skip Forward */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={handleSkipForward}
              disabled={audioState.isStopped || isInitializing || !!audioState.error}
              aria-label="Skip forward 10 seconds"
            >
              <SkipForward className="h-4 w-4" />
            </Button>

            {/* Stop */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={handleStop}
              disabled={audioState.isStopped || isInitializing || !!audioState.error}
              aria-label="Stop"
            >
              <Square className="h-4 w-4" />
            </Button>

            {/* Volume Control - Inline */}
            <div className="flex items-center gap-1 ml-auto">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleMuteToggle}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>
              <div className="w-20 hidden sm:block">
                <Slider
                  value={[isMuted ? 0 : volume]}
                  onValueChange={handleVolumeChange}
                  min={0}
                  max={1}
                  step={0.05}
                  className="cursor-pointer"
                  aria-label="Volume"
                />
              </div>
            </div>

            {/* Speed Display */}
            <div className="text-xs font-medium text-muted-foreground min-w-[32px] text-center hidden sm:block">
              {rate.toFixed(1)}x
            </div>

            {/* Advanced Settings Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => setShowAdvanced(!showAdvanced)}
              aria-label="Advanced settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress Bar - Draggable */}
          {(audioState.isPlaying || audioState.progress > 0) && (
            <div className="space-y-1">
              <Slider
                value={[audioState.progress]}
                onValueChange={handleProgressSeek}
                min={0}
                max={100}
                step={0.1}
                className="cursor-pointer"
                aria-label="Audio progress"
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatTime(audioState.currentTime)}</span>
                <span>{formatTime(audioState.duration)}</span>
              </div>
            </div>
          )}

          {/* Collapsible Advanced Controls */}
          <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
            <CollapsibleContent className="space-y-3 pt-3 border-t border-border/50">
              {/* Speed Control */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-foreground">Speed</label>
                  <span className="text-xs text-muted-foreground">{rate.toFixed(1)}x</span>
                </div>
                <SpeedPresetButtons currentRate={rate} onRateChange={handleRateChange} />
                <Slider
                  value={[rate]}
                  onValueChange={handleRateChange}
                  min={0.5}
                  max={2}
                  step={0.1}
                  className="cursor-pointer"
                  aria-label="Playback speed"
                />
              </div>

              {/* Volume Control - Mobile */}
              <div className="space-y-2 sm:hidden">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-foreground">Volume</label>
                  <span className="text-xs text-muted-foreground">
                    {Math.round((isMuted ? 0 : volume) * 100)}%
                  </span>
                </div>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  onValueChange={handleVolumeChange}
                  min={0}
                  max={1}
                  step={0.05}
                  className="cursor-pointer"
                  aria-label="Volume"
                />
              </div>

              {/* Section Navigation */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Sections</label>
                <SectionNavigationButtons
                  content={text}
                  currentWordIndex={audioState.currentWordIndex}
                  duration={audioState.duration}
                  onSeek={handleSeek}
                  isPlaying={audioState.isPlaying}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </Card>
    </>
  );
};

export default WorldClassAudioPlayer;
